import { useEffect, useState, useRef } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { IconCheck, IconX, IconArrowLeft } from '@tabler/icons-react';

import { formatSubscriptionPriceCurrency, getDiscountMeta } from '@utils/utils';
import { useCaseSubscriptionPlans } from '@shared/hooks/useCaseSubscriptionPlans';
import {
  useAlaCarteProducts,
  AlaCarteProduct,
} from '@shared/hooks/useAlaCarteProducts';
import { CasePlan } from '@app/types/payment/subscription';
import {
  productOf,
  productsOf,
  plansForProduct,
  tierOf,
  planInclusions,
  annualSavingPercent,
} from '@shared/utils/plan-catalogue';
import { CaseSubscriptionApi } from '@api/case-subscription/caseSubscription';
import { getApiErrorMessage } from '@shared/utils/handle-backend-error';
import PaymentMethodSelector from '../components/PaymentMethodSelector';

/**
 * Whether à la carte packages can be bought from the plan picker.
 *
 * Off while the per-product plans launch, so the picker offers one way to buy.
 * The tab and its contents are left in place rather than deleted: customers who
 * already hold à la carte credits keep seeing and spending them in settings,
 * and turning the option back on is this one flag.
 */
const SHOW_ALA_CARTE = false;

interface PlansPreviewModalProps {
  showModal: boolean;
  onClose: () => void;
  onPlanSelected?: (planPriceId: number, stripePriceId: string) => void;
  onTrialStarted?: () => void;
  onAlaCarteSelected?: (paymentIntentData: {
    client_secret: string;
    payment_intent_id: string;
    items: any[];
  }) => void;
  /** Optional callback when a direct à la carte purchase is successful */
  onAlaCartePurchaseSuccess?: () => void | Promise<void>;
  /** Name of the currently active subscription plan (for highlighting) */
  currentPlanName?: string | null;
  /** Active case limit of the current plan, used to distinguish tiers (e.g. 3 vs 10 cases) */
}

interface CartItem {
  id: string;
  name: string;
  price: number;
  type: 'case-plan' | 'trial' | 'a-la-carte';
  description: string;
  quantity?: number;
  // Optional identity for paid plan checkout
  planPriceId?: number;
  stripePriceId?: string;
}

type CartType = 'case-plan' | 'a-la-carte' | null;

function PlansPreviewModal(props: PlansPreviewModalProps) {
  const {
    showModal,
    onClose,
    onPlanSelected,
    onTrialStarted,
    onAlaCarteSelected,
    onAlaCartePurchaseSuccess,
    currentPlanName,
  } = props;
  const theme = useTheme();
  const { trialPlan, paidPlans, loading, error } = useCaseSubscriptionPlans();
  const {
    products: alaCarteProducts,
    loading: alaCarteLoading,
    error: alaCarteError,
  } = useAlaCarteProducts();
  // Identity is the plan id. It used to be active_case_limit, which is 0 on
  // every per-product plan, so all tiers matched the same plan and whichever
  // sorted first was what the customer actually bought.
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [hasUserSelectedTier, setHasUserSelectedTier] =
    useState<boolean>(false); // Track if user has manually selected
  const initializedRef = useRef<boolean>(false); // Track if we've initialized the tier
  const [billingPeriod, setBillingPeriod] = useState<'Month' | 'Year'>('Month');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'case-plans' | 'a-la-carte'>(
    'case-plans'
  );

  // Shopping cart state
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartType, setCartType] = useState<CartType>(null);

  // Payment method selection state
  const [showPaymentMethodSelection, setShowPaymentMethodSelection] =
    useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  // Trial eligibility (one-time only): disabled if user ever had any subscription
  const [trialEligible, setTrialEligible] = useState<boolean | null>(null);
  const [loadingTrialEligibility, setLoadingTrialEligibility] =
    useState<boolean>(false);

  useEffect(() => {
    if (!showModal) return;
    let mounted = true;
    setLoadingTrialEligibility(true);
    CaseSubscriptionApi.getTrialEligibility()
      .then((res) => {
        if (mounted) setTrialEligible(Boolean(res?.eligible));
      })
      .catch(() => {
        if (mounted) setTrialEligible(null);
      })
      .finally(() => {
        if (mounted) setLoadingTrialEligibility(false);
      });
    return () => {
      mounted = false;
    };
  }, [showModal]);

  // When modal opens, initialize selected tier from current plan's case limit if provided
  useEffect(() => {
    if (!showModal) {
      // Reset when modal closes
      setHasUserSelectedTier(false);
      initializedRef.current = false;
      return;
    }

    // Only initialize once when modal opens, we have plans, and user hasn't manually selected
    if (
      !initializedRef.current &&
      !hasUserSelectedTier &&
      paidPlans.length > 0
    ) {
      initializedRef.current = true;

      const current = currentPlanName
        ? paidPlans.find((p) => p.name === currentPlanName)
        : undefined;
      const initial = current || paidPlans[0];
      setSelectedPlanId(initial.id);
      setSelectedProduct(productOf(initial));
    }
  }, [showModal, currentPlanName, paidPlans, hasUserSelectedTier]);

  // Products offered, and the tiers within the chosen one.
  const products = productsOf(paidPlans);
  const activeProduct =
    selectedProduct && products.includes(selectedProduct)
      ? selectedProduct
      : products[0];
  const tierPlans = activeProduct
    ? plansForProduct(paidPlans, activeProduct)
    : paidPlans;

  // Resolved by id, so each tier is its own plan.
  const selectedPlan =
    paidPlans.find((p) => p.id === selectedPlanId) ||
    tierPlans[0] ||
    paidPlans[0];
  const selectedPrice = selectedPlan?.prices.find(
    (p) => p.period === billingPeriod
  );
  const monthlyPrice = selectedPlan?.prices?.find((p) => p.period === 'Month');
  const annualPrice = selectedPlan?.prices?.find((p) => p.period === 'Year');
  const hasAnnualOption = !!monthlyPrice && !!annualPrice;
  const annualSaving = hasAnnualOption
    ? annualSavingPercent(monthlyPrice?.price, annualPrice?.price)
    : null;
  const discount = selectedPrice
    ? getDiscountMeta(
        selectedPrice.list_price as any,
        selectedPrice.price as any
      )
    : ({ hasDiscount: false } as any);

  // Determine if the currently selected card/tier matches the user's actual active subscription
  const isCurrentPlanTier =
    !!currentPlanName &&
    !!selectedPlan &&
    selectedPlan.name === currentPlanName;

  const handleStartTrial = () => {
    if (!trialPlan) return;
    if (trialEligible === false) {
      setErrorMessage('Free trial is not available for your account.');
      return;
    }

    setErrorMessage(null);

    // Add trial to cart
    const trialItem: CartItem = {
      id: 'free-trial',
      name: 'Free Trial',
      price: 0,
      type: 'trial',
      description: `Full access for ${trialPlan.trial_duration_days} days`,
    };

    setCart([trialItem]);
    setCartType('case-plan');
  };

  const handleSelectPaidPlan = () => {
    if (!selectedPlan || !selectedPrice) return;

    setErrorMessage(null);

    // Add case plan to cart
    const planItem: CartItem = {
      id: `case-plan-${selectedPlan.id}`,
      name: selectedPlan.name,
      price: Number(selectedPrice.price),
      type: 'case-plan',
      description: planInclusions(selectedPlan).join(' · '),
      planPriceId: selectedPrice.id,
      stripePriceId: selectedPrice.stripe_price_id,
    };

    setCart([planItem]);
    setCartType('case-plan');
  };

  const getPlanFeatures = (plan: CasePlan) => {
    if (Array.isArray(plan.features) && plan.features.length > 0) {
      return plan.features;
    }
    return [];
  };

  const getTrialFeatures = () => {
    if (
      trialPlan &&
      Array.isArray(trialPlan.features) &&
      trialPlan.features.length > 0
    ) {
      return trialPlan.features;
    }
    return [];
  };

  const getAlaCarteFeatures = (product: AlaCarteProduct): string[] => {
    // Only show features from backend; no credits or groups in the list
    return Array.isArray(product.features) ? product.features : [];
  };

  const handleAlaCarteAddToCart = (product: AlaCarteProduct) => {
    setErrorMessage(null);

    const productIdStr = product.product_id;

    // Choosing a one-time package while a plan is selected replaces the plan, the
    // same way choosing a plan replaces these. Previously the user had to find and
    // press "Clear Cart" before they were permitted to select the other type, and
    // the cards were disabled meanwhile with no inline explanation. See ZLA-007.
    if (cartType === 'case-plan') {
      setCart([
        {
          id: productIdStr,
          name: product.name,
          price: parseFloat(product.price),
          type: 'a-la-carte',
          description: product.description,
          quantity: 1,
        },
      ]);
      setCartType('a-la-carte');
      return;
    }

    // Check if item already in cart
    const existingItemIndex = cart.findIndex(
      (item) => item.id === productIdStr
    );

    if (existingItemIndex >= 0) {
      // Increment quantity
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity =
        (updatedCart[existingItemIndex].quantity || 1) + 1;
      setCart(updatedCart);
    } else {
      // Add new item
      const cartItem: CartItem = {
        id: productIdStr,
        name: product.name,
        price: parseFloat(product.price),
        type: 'a-la-carte',
        description: product.description,
        quantity: 1,
      };
      setCart([...cart, cartItem]);
      setCartType('a-la-carte');
    }
  };

  // handleRemoveFromCart and handleClearCart went with the cart panel. Choosing a
  // plan or package now replaces the selection outright, so there is nothing to
  // remove item-by-item and nothing to clear before choosing something else.

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      return total + item.price * (item.quantity || 1);
    }, 0);
  };

  const isItemInCart = (itemId: string) => {
    return cart.some((item) => item.id === itemId);
  };

  // isCasePlanSelected / isAlaCarteSelected were removed with the mutual-exclusion
  // disabling they existed to drive. Selecting one type now replaces the other, so
  // nothing needs to ask "is the other kind chosen?" in order to grey a card out.

  const overlayZ = (theme.zIndex.modal || 1300) + 1000;

  if (loading) {
    return (
      <Dialog
        open={showModal}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 300,
          }}
        >
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog
        open={showModal}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Button onClick={onClose} variant="contained" sx={{ mt: 2 }}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={showModal}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableScrollLock
      sx={{ zIndex: overlayZ }}
      BackdropProps={{ sx: { zIndex: overlayZ - 1 } }}
      PaperProps={{
        sx: {
          zIndex: overlayZ + 1,
          display: 'flex',
          flexDirection: 'column',
          height: '80vh',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div />
          <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            Zlayt AI Plans Overview
          </Typography>
          <IconButton onClick={onClose} aria-label="close plans preview">
            <IconX />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflowY: 'auto' }}>
        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 0.5, mb: 3, color: 'text.secondary' }}
        >
          Choose the perfect plan for your legal practice. All recurring plans
          can be cancelled anytime.
        </Typography>

        {/* Tab Toggle for Case Plans / À la Carte */}
        {SHOW_ALA_CARTE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <ToggleButtonGroup
              value={activeTab}
              exclusive
              onChange={(_e, newValue) => {
                if (newValue !== null) {
                  setActiveTab(newValue);
                  setErrorMessage(null);
                }
              }}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  px: 4,
                  py: 1,
                  border: 'none',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="case-plans">Case Plans</ToggleButton>
              <ToggleButton value="a-la-carte">À la Carte</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {errorMessage && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          </Box>
        )}

        {/* Case Plans Tab Content */}
        {activeTab === 'case-plans' && (
          <Grid
            container
            spacing={3}
            sx={{ mt: 1, mb: 4 }}
            justifyContent="center"
            alignItems="stretch"
          >
            {/* Free Trial Card - Using existing subscription styling */}
            {trialPlan && (
              <Grid
                item
                xs={12}
                sm={6}
                md={5}
                key="trial"
                sx={{ display: 'flex' }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    border:
                      currentPlanName && trialPlan.name === currentPlanName
                        ? '2px solid'
                        : '1px solid',
                    borderColor:
                      currentPlanName && trialPlan.name === currentPlanName
                        ? theme.palette.success.main
                        : theme.palette.mode === 'dark'
                          ? 'background.default'
                          : alpha(theme.palette.primary.light, 0.75),
                    borderRadius: '8px',
                    boxShadow: 5,
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    {/* Plan Name */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      >
                        {trialPlan.name}
                      </Typography>
                    </Box>

                    {/* Price and Free Badge on same line */}
                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: 'primary.main' }}
                      >
                        {formatSubscriptionPriceCurrency(0)}
                      </Typography>
                      <Chip
                        // The term belongs next to the price, not only in the cart
                        // line item. Nobody should click "Start Free Trial" without
                        // knowing whether it runs 7 days or 30. See ZLA-004.
                        label={
                          trialPlan.trial_duration_days
                            ? `Free for ${trialPlan.trial_duration_days} days`
                            : 'Free'
                        }
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: 'success.main',
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    {/* Description */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {trialPlan.description}
                      </Typography>
                    </Box>

                    {/* Features List */}
                    <Box sx={{ flexGrow: 1, mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                      >
                        Includes:
                      </Typography>
                      <List
                        sx={{
                          m: 0,
                          p: 0,
                          '& .MuiListItem-root': {
                            px: 0,
                            py: 0.5,
                            minHeight: 'auto',
                          },
                          '& .MuiListItemIcon-root': {
                            minWidth: 24,
                            mr: 1,
                          },
                          '& .MuiListItemText-root': {
                            m: 0,
                          },
                        }}
                      >
                        {getTrialFeatures().map((feature) => (
                          <ListItem key={feature}>
                            <ListItemIcon>
                              <IconCheck
                                size={16}
                                color={theme.palette.success.main}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: 'text.secondary',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    {/* CTA Button */}
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleStartTrial}
                        disabled={
                          isProcessing ||
                          // NOT disabled when a la carte items are chosen. Disabling
                          // here is what made "Start Free Trial" silently inert: the
                          // click landed on a disabled button and nothing happened,
                          // with no error and no explanation. Choosing the trial now
                          // simply replaces the other selection. See ZLA-007, ZLA-008.
                          isItemInCart('free-trial') ||
                          Boolean(
                            currentPlanName &&
                              trialPlan.name === currentPlanName
                          ) || // Disable if current plan
                          trialEligible === false ||
                          loadingTrialEligibility
                        }
                        sx={{
                          bgcolor: isItemInCart('free-trial')
                            ? 'primary.main'
                            : undefined,
                          '&.Mui-disabled':
                            isItemInCart('free-trial') ||
                            (currentPlanName &&
                              trialPlan.name === currentPlanName)
                              ? {
                                  bgcolor:
                                    currentPlanName &&
                                    trialPlan.name === currentPlanName
                                      ? 'success.main'
                                      : 'primary.main',
                                  color: 'white',
                                  opacity: 0.9,
                                }
                              : undefined,
                        }}
                      >
                        {currentPlanName && trialPlan.name === currentPlanName
                          ? 'Current Plan'
                          : trialEligible === false
                            ? 'Trial Unavailable'
                            : isItemInCart('free-trial')
                              ? // Was "Case Plan Selected", on the free trial card.
                                // That mislabel is why the plan card, the notice bar
                                // and the cart appeared to disagree about what had
                                // been chosen. See ZLA-008.
                                'Free Trial Selected'
                              : isProcessing
                                ? 'Starting...'
                                : // This button CHOOSES the trial; the footer button
                                  // starts it. Both used to read "Start Free Trial",
                                  // so pressing the card one appeared to do nothing.
                                  'Choose Free Trial'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Single Plan Card with Tier Badge Selection */}
            {paidPlans.length > 0 && selectedPlan && selectedPrice && (
              <Grid item xs={12} sm={6} md={5} sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    p: 2.5,
                    border:
                      currentPlanName && selectedPlan?.name === currentPlanName
                        ? '2px solid'
                        : '1px solid',
                    borderColor:
                      currentPlanName && selectedPlan?.name === currentPlanName
                        ? theme.palette.success.main
                        : theme.palette.mode === 'dark'
                          ? 'background.default'
                          : alpha(theme.palette.primary.light, 0.75),
                    borderRadius: '8px',
                    boxShadow: 5,
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    {/* Plan Name */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      >
                        {selectedPlan?.name || 'Zlayt AI – Case Plan'}
                      </Typography>
                    </Box>

                    {/* Price and Monthly Badge */}
                    <Box sx={{ mb: 2 }}>
                      {discount.hasDiscount ? (
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textDecoration: 'line-through' }}
                          >
                            {discount.listPriceFormatted}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 700, color: 'primary.main' }}
                            >
                              {discount.priceFormatted}
                            </Typography>
                            <Chip
                              // Follows the chosen period. It was hardcoded to
                              // Monthly, so annual prices were displayed with a
                              // monthly label once annual became selectable.
                              label={
                                billingPeriod === 'Year' ? 'Annual' : 'Monthly'
                              }
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ color: 'success.main', fontWeight: 600 }}
                          >
                            {discount.percentOff}% discount • Save{' '}
                            {discount.youSave}
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: 'primary.main' }}
                          >
                            {selectedPrice
                              ? formatSubscriptionPriceCurrency(
                                  Number(selectedPrice.price)
                                )
                              : '$0'}
                          </Typography>
                          <Chip
                            label={
                              billingPeriod === 'Year' ? 'Annual' : 'Monthly'
                            }
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Product selection. Plans are sold per product, so the
                        product is chosen before the tier. */}
                    {products.length > 1 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                        >
                          Which product do you need?
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {products.map((product) => {
                            const isProductSelected = activeProduct === product;
                            return (
                              <Chip
                                key={product}
                                label={product}
                                clickable
                                aria-pressed={isProductSelected}
                                onClick={() => {
                                  setSelectedProduct(product);
                                  const first = plansForProduct(
                                    paidPlans,
                                    product
                                  )[0];
                                  if (first) setSelectedPlanId(first.id);
                                  setHasUserSelectedTier(true);
                                }}
                                color={
                                  isProductSelected ? 'primary' : 'default'
                                }
                                variant={
                                  isProductSelected ? 'filled' : 'outlined'
                                }
                                sx={{
                                  fontWeight: isProductSelected ? 600 : 500,
                                  cursor: 'pointer',
                                  borderWidth: isProductSelected ? 0 : 1.5,
                                  borderColor: 'primary.main',
                                  color: isProductSelected
                                    ? undefined
                                    : 'primary.main',
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Monthly or annual. The saving is computed from the two
                        prices rather than asserted, so it cannot drift. */}
                    {hasAnnualOption && (
                      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Chip
                          label="Monthly"
                          clickable
                          aria-pressed={billingPeriod === 'Month'}
                          onClick={() => setBillingPeriod('Month')}
                          color={
                            billingPeriod === 'Month' ? 'primary' : 'default'
                          }
                          variant={
                            billingPeriod === 'Month' ? 'filled' : 'outlined'
                          }
                          sx={{ cursor: 'pointer' }}
                        />
                        <Chip
                          label={
                            annualSaving
                              ? `Annual  ·  save ${annualSaving}%`
                              : 'Annual'
                          }
                          clickable
                          aria-pressed={billingPeriod === 'Year'}
                          onClick={() => setBillingPeriod('Year')}
                          color={
                            billingPeriod === 'Year' ? 'primary' : 'default'
                          }
                          variant={
                            billingPeriod === 'Year' ? 'filled' : 'outlined'
                          }
                          sx={{ cursor: 'pointer' }}
                        />
                      </Box>
                    )}

                    {/* Tier Selection Badges */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                      >
                        How much do you need each month?
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {tierPlans.map((plan) => {
                          const isSelected = selectedPlan?.id === plan.id;
                          const tierPrice = plan.prices?.find(
                            (p) => p.period === billingPeriod
                          );
                          const tierLabel = tierOf(plan) || plan.name;
                          return (
                            <Chip
                              key={plan.id}
                              // Price on the pill so the relationship between the
                              // tier and its cost is immediate, and so the price
                              // above is visibly tied to the pill that is chosen.
                              label={
                                tierPrice
                                  ? `${tierLabel}  ·  ${formatSubscriptionPriceCurrency(Number(tierPrice.price))}`
                                  : tierLabel
                              }
                              clickable
                              aria-pressed={isSelected}
                              onClick={() => {
                                setSelectedPlanId(plan.id);
                                setHasUserSelectedTier(true); // Mark that user has manually selected
                              }}
                              color={isSelected ? 'primary' : 'default'}
                              variant={isSelected ? 'filled' : 'outlined'}
                              sx={{
                                fontWeight: isSelected ? 600 : 500,
                                cursor: 'pointer',
                                // Unselected pills carry a visible border and hover
                                // state so the whole set reads as interactive rather
                                // than as three labels describing the plan.
                                borderWidth: isSelected ? 0 : 1.5,
                                borderColor: 'primary.main',
                                color: isSelected ? undefined : 'primary.main',
                                '&:hover': {
                                  bgcolor: isSelected
                                    ? undefined
                                    : alpha(theme.palette.primary.main, 0.12),
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>

                    {/* Features List */}
                    <Box sx={{ flexGrow: 1, mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                      >
                        Includes:
                      </Typography>
                      <List
                        sx={{
                          m: 0,
                          p: 0,
                          '& .MuiListItem-root': {
                            px: 0,
                            py: 0.5,
                            minHeight: 'auto',
                          },
                          '& .MuiListItemIcon-root': {
                            minWidth: 24,
                            mr: 1,
                          },
                          '& .MuiListItemText-root': {
                            m: 0,
                          },
                        }}
                      >
                        {getPlanFeatures(selectedPlan).map((feature) => (
                          <ListItem key={feature}>
                            <ListItemIcon>
                              <IconCheck
                                size={16}
                                color={theme.palette.success.main}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: 'text.secondary',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    {/* CTA Button */}
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleSelectPaidPlan}
                        disabled={
                          isProcessing ||
                          !selectedPrice?.stripe_price_id ||
                          // Not disabled by an a la carte selection: choosing a plan
                          // replaces it rather than requiring the user to clear a cart
                          // first. See ZLA-007.
                          isItemInCart(`case-plan-${selectedPlan?.id}`) ||
                          isCurrentPlanTier // Disable only for the exact current tier
                        }
                        sx={{
                          bgcolor: isItemInCart(`case-plan-${selectedPlan?.id}`)
                            ? 'primary.main'
                            : undefined,
                          '&.Mui-disabled':
                            isItemInCart(`case-plan-${selectedPlan?.id}`) ||
                            isCurrentPlanTier
                              ? {
                                  bgcolor: isCurrentPlanTier
                                    ? 'success.main'
                                    : 'primary.main',
                                  color: 'white',
                                  opacity: 0.9,
                                }
                              : undefined,
                        }}
                      >
                        {isCurrentPlanTier
                          ? 'Current Plan'
                          : isItemInCart(`case-plan-${selectedPlan?.id}`)
                            ? 'Plan Selected'
                            : currentPlanName
                              ? 'Upgrade Plan'
                              : // "Add to Cart" framed a single plan choice as
                                // assembling a basket. Nobody is buying several
                                // plans. See ZLA-007.
                                'Select Plan'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
            {/* Money Back Guarantee aligned with grid */}
            <Grid item xs={10}>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <strong>Money Back Guarantee:</strong> If you are not 100%
                  satisfied with your purchase after completing all of your use
                  cases we will offer you a 100% money back guarantee
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* À la Carte Tab Content */}
        {SHOW_ALA_CARTE && activeTab === 'a-la-carte' && (
          <Grid container justifyContent="center">
            <Grid item xs={10}>
              <Grid
                container
                spacing={3}
                sx={{ mt: 1, mb: 4 }}
                alignItems="stretch"
              >
                {alaCarteLoading ? (
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                  >
                    <CircularProgress />
                  </Grid>
                ) : alaCarteError ? (
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                  >
                    <Typography color="error">{alaCarteError}</Typography>
                  </Grid>
                ) : !alaCarteProducts ||
                  !Array.isArray(alaCarteProducts) ||
                  alaCarteProducts.length === 0 ? (
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                  >
                    <Typography color="text.secondary">
                      No à la carte products available
                    </Typography>
                  </Grid>
                ) : (
                  alaCarteProducts.map((product) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={product.id}
                      sx={{ display: 'flex' }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          border: '1px solid',
                          borderColor:
                            theme.palette.mode === 'dark'
                              ? 'background.default'
                              : alpha(theme.palette.primary.light, 0.75),
                          borderRadius: '8px',
                          boxShadow: 5,
                          width: '100%',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            width: '100%',
                          }}
                        >
                          {/* Product Name */}
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                              }}
                            >
                              {product.name}
                            </Typography>
                          </Box>

                          {/* Price and One-Time Badge on same line */}
                          <Box
                            sx={{
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 700, color: 'primary.main' }}
                            >
                              {formatSubscriptionPriceCurrency(
                                parseFloat(product.price)
                              )}
                            </Typography>
                            <Chip
                              label="Single"
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          </Box>

                          {/* Description */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {product.description}
                            </Typography>
                          </Box>

                          {/* Includes Label */}
                          <Box sx={{ mb: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: 'text.primary' }}
                            >
                              Includes:
                            </Typography>
                          </Box>

                          {/* Features List */}
                          <Box sx={{ flexGrow: 1, mb: 2 }}>
                            <List
                              sx={{
                                m: 0,
                                p: 0,
                                '&> li': {
                                  px: 0,
                                  py: 0.5,
                                },
                              }}
                              component="ul"
                            >
                              {getAlaCarteFeatures(product).map(
                                (feature: string, index: number) => (
                                  <ListItem key={index}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <IconCheck
                                        size={18}
                                        color={theme.palette.primary.main}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={feature}
                                      primaryTypographyProps={{
                                        variant: 'body2',
                                        sx: { fontSize: '0.875rem' },
                                      }}
                                    />
                                  </ListItem>
                                )
                              )}
                            </List>
                          </Box>

                          {/* Select button */}
                          <Box sx={{ mt: 'auto' }}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              onClick={() => handleAlaCarteAddToCart(product)}
                              // Never disabled by a case plan being selected. Greying
                              // these out with the reason stated only in a notice below
                              // read as a fault rather than an undoable choice, and
                              // forced the user to undo before they were allowed to
                              // look at the alternative. See ZLA-007.
                              disabled={isProcessing}
                            >
                              {isItemInCart(product.product_id)
                                ? 'Added'
                                : 'Select'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))
                )}
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Your selection */}
        {/* The shopping-cart panel and its "in selection" notice used to sit here.
            Both are gone: the footer names the current selection persistently, and a
            cart is the wrong model for picking one plan. Only the payment-method
            step remains, which is a real step rather than a running basket.
            See ZLA-007. */}
        {/* Payment Method Selection View */}
        {showPaymentMethodSelection && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              startIcon={<IconArrowLeft size={16} />}
              onClick={() => {
                setShowPaymentMethodSelection(false);
                setSelectedPaymentMethodId(null);
              }}
              sx={{ mb: 2 }}
            >
              Back to cart
            </Button>
            <PaymentMethodSelector
              onSelect={(pmId) => setSelectedPaymentMethodId(pmId)}
              onAddNew={() => {
                // Close this modal and proceed to add payment method form
                setShowPaymentMethodSelection(false);
                // For case plans (including trial), go to payment form
                if (cartType === 'case-plan') {
                  const item = cart[0];
                  // Handle trial - go to payment form with trial mode
                  if (item.type === 'trial') {
                    // Trigger the payment form with trial checkout type
                    // This will collect card details for the trial
                    if (onPlanSelected) {
                      // Use -1 as sentinel for trial
                      onPlanSelected(-1, 'trial');
                    }
                  } else if (
                    item.type === 'case-plan' &&
                    item.planPriceId &&
                    item.stripePriceId
                  ) {
                    if (onPlanSelected) {
                      onPlanSelected(item.planPriceId, item.stripePriceId);
                    }
                  }
                } else if (cartType === 'a-la-carte' && onAlaCarteSelected) {
                  // For à la carte, create payment intent and go to payment form
                  const items = cart
                    .filter((ci) => ci.type === 'a-la-carte')
                    .map((ci) => ({
                      product_id: ci.id,
                      quantity: ci.quantity || 1,
                    }));
                  import('@api/alacarte/alaCarteApi').then(
                    async ({ AlaCarteApi }) => {
                      try {
                        const { client_secret, payment_intent_id } =
                          await AlaCarteApi.createPaymentIntent(items);
                        onAlaCarteSelected({
                          client_secret,
                          payment_intent_id,
                          items,
                        });
                      } catch (e: any) {
                        setErrorMessage(
                          getApiErrorMessage(e, 'Failed to create payment')
                        );
                      }
                    }
                  );
                }
              }}
              selectedPaymentMethodId={selectedPaymentMethodId || undefined}
            />
          </Box>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          display="block"
          sx={{ mt: 2 }}
        >
          All prices in USD. Recurring plans include cancel anytime guarantee.
        </Typography>
      </DialogContent>
      {/* Primary action bar. This button used to sit at the very bottom of a long
            scrolling list, below every plan card and the cart panel, while the card
            buttons above it were also labelled "Start Free Trial" but only selected a
            plan. Choosing a plan therefore appeared to do nothing until you scrolled
            to the bottom and found the real action. It now lives outside the scroll
            area, is always visible, and names what is selected beside it. One action,
            always reachable. See ZLA-007. */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {cart.length === 0 ? 'Nothing selected yet' : 'Your selection'}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {cart.length === 0
              ? 'Choose a plan or package to continue'
              : cartType === 'case-plan'
                ? `${cart[0].name} - ${
                    cart[0].price > 0
                      ? `${formatSubscriptionPriceCurrency(cart[0].price)} / ${billingPeriod.toLowerCase()}`
                      : 'Free'
                  }`
                : `${cart.length} package${cart.length > 1 ? 's' : ''} - ${formatSubscriptionPriceCurrency(calculateTotal())}`}
          </Typography>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 240 } }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={
              isProcessing ||
              cart.length === 0 ||
              (showPaymentMethodSelection && !selectedPaymentMethodId)
            }
            startIcon={
              isProcessing ? (
                <CircularProgress color="inherit" size={18} />
              ) : undefined
            }
            onClick={async () => {
              if (cart.length === 0 || isProcessing) return;

              const item = cart[0];

              // For all purchases (including trial), show payment method selection first
              // because backend requires a payment method to be set
              if (!showPaymentMethodSelection && onAlaCartePurchaseSuccess) {
                setShowPaymentMethodSelection(true);
                return;
              }

              // For trial with payment method selected, start trial
              if (
                cartType === 'case-plan' &&
                item.type === 'trial' &&
                showPaymentMethodSelection &&
                selectedPaymentMethodId
              ) {
                setIsProcessing(true);
                if (onTrialStarted) {
                  try {
                    await onTrialStarted();
                  } catch (e) {
                    /* handled upstream */
                  }
                }
                setIsProcessing(false);
                return;
              }

              console.log(
                '[PlansPreviewModal] Processing checkout. cartType:',
                cartType,
                'selectedPaymentMethodId:',
                selectedPaymentMethodId
              );
              setIsProcessing(true);

              // Handle different cart types
              if (cartType === 'case-plan') {
                if (item.type === 'trial') {
                  // The trial had no branch here at all. Everything below matches
                  // item.type === 'case-plan', so selecting the free trial and
                  // pressing the button fell straight through this handler and did
                  // nothing: no error, no navigation, no request. The only trial
                  // path that existed required a payment method to have been picked
                  // first, which never happens in the signup flow.
                  // -1 is the sentinel the parent maps to checkoutType 'trial',
                  // which closes this modal and opens the card form.
                  if (onPlanSelected) {
                    await onPlanSelected(-1, 'trial');
                  } else if (onTrialStarted) {
                    await onTrialStarted();
                  } else {
                    setErrorMessage(
                      'Could not start the free trial. Please try again.'
                    );
                  }
                  setIsProcessing(false);
                  return;
                }
                if (item.type === 'case-plan') {
                  // Determine planPriceId and stripePriceId
                  let pId = item.planPriceId;
                  let sId = item.stripePriceId;

                  if (!pId || !sId) {
                    // The cart id carries the plan id. It used to carry the
                    // case limit, which is 0 on every per-product plan and so
                    // resolved every tier to the same plan at checkout.
                    const match = item.id.match(/case-plan-(\d+)/);
                    const cartPlanId = match ? parseInt(match[1], 10) : null;
                    const planFromCart = cartPlanId
                      ? paidPlans.find((p) => p.id === cartPlanId)
                      : null;
                    const priceFromCart = planFromCart?.prices.find(
                      (p) => p.period === billingPeriod
                    );
                    pId = priceFromCart?.id;
                    sId = priceFromCart?.stripe_price_id;
                  }

                  // Use current selection state as last resort
                  if (!pId || !sId) {
                    pId = selectedPrice?.id;
                    sId = selectedPrice?.stripe_price_id;
                  }

                  if (!pId || !sId) {
                    setErrorMessage(
                      'Unable to resolve selected plan for checkout. Please reselect the plan.'
                    );
                    setIsProcessing(false);
                    return;
                  }

                  // Try direct upgrade if onAlaCartePurchaseSuccess is provided (authenticated flow)
                  if (onAlaCartePurchaseSuccess && selectedPaymentMethodId) {
                    console.log(
                      '[PlansPreviewModal] Attempting direct plan upgrade for planPriceId:',
                      pId,
                      'with payment method:',
                      selectedPaymentMethodId
                    );
                    try {
                      await CaseSubscriptionApi.upgrade(
                        pId,
                        selectedPaymentMethodId
                      );
                      console.log(
                        '[PlansPreviewModal] Direct upgrade successful!'
                      );
                      setShowPaymentMethodSelection(false);
                      setSelectedPaymentMethodId(null);
                      await onAlaCartePurchaseSuccess();
                      setIsProcessing(false);
                      return;
                    } catch (e: any) {
                      const errorData = e?.response?.data || {};
                      const checkoutRequired =
                        errorData.checkout_required === true;

                      if (checkoutRequired) {
                        console.log(
                          '[PlansPreviewModal] Direct upgrade requires card entry, falling back to payment form'
                        );
                        // Fall through to onPlanSelected below
                      } else {
                        console.error(
                          '[PlansPreviewModal] Direct upgrade failed:',
                          errorData.detail || e.message
                        );
                        setErrorMessage(
                          getApiErrorMessage(e, 'Failed to upgrade plan')
                        );
                        setIsProcessing(false);
                        return;
                      }
                    }
                  }

                  // Fallback: proceed to payment details form
                  if (onPlanSelected) {
                    try {
                      await onPlanSelected(pId, sId);
                    } catch (e) {
                      /* handled upstream */
                    }
                  }
                }
              } else if (cartType === 'a-la-carte') {
                // Create Payment Intent for in-app payment (new flow)
                try {
                  const items = cart
                    .filter((ci) => ci.type === 'a-la-carte')
                    .map((ci) => ({
                      product_id: ci.id,
                      quantity: ci.quantity || 1,
                    }));

                  console.log(
                    '[PlansPreviewModal] Starting à la carte checkout for items:',
                    items
                  );

                  // Import AlaCarteApi
                  const { AlaCarteApi } = await import(
                    '@api/alacarte/alaCarteApi'
                  );

                  // 1. Try direct purchase if user is likely authenticated (onAlaCartePurchaseSuccess exists)
                  if (onAlaCartePurchaseSuccess && selectedPaymentMethodId) {
                    console.log(
                      '[PlansPreviewModal] Attempting direct purchase with payment method:',
                      selectedPaymentMethodId
                    );
                    try {
                      const result = await AlaCarteApi.purchaseDirect(
                        items,
                        selectedPaymentMethodId
                      );
                      console.log(
                        '[PlansPreviewModal] Direct purchase result:',
                        result
                      );
                      if (result.success) {
                        console.log(
                          '[PlansPreviewModal] Direct purchase successful!'
                        );
                        setShowPaymentMethodSelection(false);
                        setSelectedPaymentMethodId(null);
                        await onAlaCartePurchaseSuccess();
                        setIsProcessing(false);
                        return;
                      }
                      // If requires_action (3DS), we still need the payment modal
                      if (result.requires_action) {
                        console.log(
                          '[PlansPreviewModal] Direct purchase requires action (3DS), switching to payment modal'
                        );
                        if (onAlaCarteSelected) {
                          onAlaCarteSelected({
                            client_secret: result.payment_intent_client_secret,
                            payment_intent_id: result.payment_intent_id,
                            items,
                          });
                          setIsProcessing(false);
                          return;
                        }
                      }
                      // If result doesn't have success or requires_action, something unexpected happened
                      console.warn(
                        '[PlansPreviewModal] Direct purchase returned unexpected result:',
                        result
                      );
                      // Fall through to standard flow
                    } catch (e: any) {
                      const errorData = e?.response?.data || {};
                      const errorDetail = getApiErrorMessage(
                        e,
                        'Unknown error'
                      );
                      const checkoutRequired =
                        errorData.checkout_required === true;

                      console.log(
                        '[PlansPreviewModal] Direct purchase error:',
                        {
                          detail: errorDetail,
                          checkoutRequired,
                          status: e?.response?.status,
                        }
                      );

                      // Only fall back to payment form if backend explicitly says checkout_required
                      if (checkoutRequired) {
                        console.log(
                          '[PlansPreviewModal] Backend indicates checkout required (no saved payment method), falling back to payment form'
                        );
                        // Fall through to standard flow below
                      } else {
                        // Other errors (network, server, etc.) - show error and stop
                        console.error(
                          '[PlansPreviewModal] Direct purchase failed with non-checkout error:',
                          errorDetail
                        );
                        setErrorMessage(errorDetail);
                        setIsProcessing(false);
                        return;
                      }
                    }
                  }

                  // 2. Standard flow: Create Payment Intent and show payment form
                  console.log(
                    '[PlansPreviewModal] Creating Payment Intent for standard flow...'
                  );
                  const { client_secret, payment_intent_id } =
                    await AlaCarteApi.createPaymentIntent(items);

                  console.log('[PlansPreviewModal] Payment Intent created:', {
                    payment_intent_id,
                    has_client_secret: !!client_secret,
                  });

                  // Pass payment intent data to parent via callback
                  if (onAlaCarteSelected) {
                    console.log(
                      '[PlansPreviewModal] Calling onAlaCarteSelected callback'
                    );
                    onAlaCarteSelected({
                      client_secret,
                      payment_intent_id,
                      items,
                    });
                  } else {
                    console.error(
                      '[PlansPreviewModal] onAlaCarteSelected callback is not provided!'
                    );
                    setErrorMessage(
                      'Payment flow not configured. Please contact support.'
                    );
                    setIsProcessing(false);
                    return;
                  }

                  // Parent will handle modal transition
                  console.log(
                    '[PlansPreviewModal] PI created, parent will handle modal transition'
                  );
                  setIsProcessing(false);
                  return;
                } catch (e: any) {
                  console.error(
                    '[PlansPreviewModal] Error during à la carte checkout:',
                    e
                  );
                  setErrorMessage(
                    getApiErrorMessage(e, 'Failed to start checkout')
                  );
                  setIsProcessing(false);
                  return;
                }
              }
              // If redirecting to Stripe, the page will unload; otherwise stop processing
              setIsProcessing(false);
            }}
          >
            {isProcessing
              ? 'Processing…'
              : showPaymentMethodSelection
                ? 'Confirm Payment'
                : cart[0]?.type === 'trial'
                  ? 'Start Free Trial'
                  : 'Proceed to Checkout'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export default PlansPreviewModal;
